/**
 * @author mrdoob / http://mrdoob.com/ https://github.com/mrdoob/eventdispatcher.js
 *
 * with slight modifications by mschuetz, http://potree.org
 *
 */

// The MIT License
//
// Copyright (c) 2011 Mr.doob
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

/**
 * 轻量事件派发器，按事件类型维护监听器列表并分发事件对象。
 */
export class EventDispatcher {
  _listeners: Record<string, ((event: any) => void)[]> = {}
  constructor() {
    this._listeners = {}
  }

  /**
   * 注册事件监听器，重复注册同一监听器会被忽略。
   *
   * @param type - 事件类型标识
   * @param listener - 事件触发时执行的回调
   */
  addEventListener(type: string, listener: (event: any) => void) {
    const listeners = this._listeners

    if (listeners[type] === undefined) {
      listeners[type] = []
    }

    if (listeners[type].indexOf(listener) === -1) {
      listeners[type].push(listener)
    }
  }

  /**
   * 判断监听器是否已注册在给定事件类型上。
   *
   * @param type - 事件类型标识
   * @param listener - 待检测的回调
   * @returns 存在返回 true，否则返回 false
   */
  hasEventListener(type: string, listener: (event: any) => void) {
    const listeners = this._listeners

    return listeners[type] !== undefined && listeners[type].indexOf(listener) !== -1
  }

  /**
   * 移除指定事件类型上的单个监听器。
   *
   * @param type - 事件类型标识
   * @param listener - 需要移除的回调
   */
  removeEventListener(type: string, listener: (event: any) => void) {
    const listeners = this._listeners
    const listenerArray = listeners[type]

    if (listenerArray !== undefined) {
      const index = listenerArray.indexOf(listener)

      if (index !== -1) {
        listenerArray.splice(index, 1)
      }
    }
  }

  /**
   * 清空指定事件类型上的全部监听器。
   *
   * @param type - 事件类型标识
   */
  removeEventListeners(type: string) {
    if (this._listeners[type] !== undefined) {
      delete this._listeners[type]
    }
  }

  /**
   * 向所有订阅该类型的监听器派发事件对象。
   *
   * @param event - 事件对象，需包含 type 字段；派发时会自动写入 target 指向当前派发器
   */
  dispatchEvent(event: any) {
    const listeners = this._listeners
    const listenerArray = listeners[event.type as string]

    if (listenerArray !== undefined) {
      event.target = this

      for (const listener of listenerArray.slice(0)) {
        listener.call(this, event)
      }
    }
  }
}
